import { MouseEvent, PureComponent, ReactNode } from 'react';
import { Maybe } from '../../types';

type MouseEventHandler = (e: MouseEvent<HTMLElement>) => void;

interface ToggleRenderProps {
  active: boolean;
  activeBefore: boolean;
  onClick: MouseEventHandler;
  setInactive: () => void;
  setActive: () => void;
}

interface ToggleProps {
  render: (props: ToggleRenderProps) => ReactNode;
  preventDefault?: boolean;
}

interface ToggleState {
  active: boolean;
  activeBefore: boolean;
}

export class Toggle extends PureComponent<ToggleProps, ToggleState> {
  public state = {
    active: false,
    activeBefore: false
  };

  public timeoutId: Maybe<NodeJS.Timeout> = null;

  public onClick: MouseEventHandler = e => {
    if (this.props.preventDefault) {
      e.preventDefault();
    }
    this.toggle();
  };

  public toggle = () => {
    this.setState(({ active }) => ({
      active: !active,
      activeBefore: true
    }));
  };

  public setInactive = () => {
    this.setState({
      active: false
    });
  };

  public setActive = () => {
    this.setState({
      active: true,
      activeBefore: true
    });
  };

  public componentDidUpdate() {
    this.state.active && triggerToggle(this);
  }

  public componentDidMount() {
    addToggle(this);
  }

  public componentWillUnmount() {
    removeToggle(this);
  }

  public render() {
    const { active, activeBefore } = this.state;
    const { render } = this.props;
    const children = render({
      active,
      activeBefore,
      onClick: this.onClick,
      setActive: this.setActive,
      setInactive: this.setInactive
    });
    return children;
  }
}

const toggles: Set<Toggle> = new Set();

const addToggle = (toggle: Toggle) => toggles.add(toggle);

const removeToggle = (toggle: Toggle) => toggles.delete(toggle);

const triggerToggle = (toggle: Toggle) => {
  toggles.forEach(t => {
    if (t !== toggle) {
      t.setInactive();
    }
  });
};
